import { isSupportViewActive } from "~/lib/impersonation";
import {
  claimTrackingFlag,
  hasMarketingConsent,
  trackingFlagClaimed,
} from "~/lib/tracking";

declare global {
  interface Window {
    // The MasterTag's own tracking object. It reads `Sale` to build its
    // conversion call (tt=ia/js) either on load, or immediately via `run()`
    // when the tag is already present (see `fireAwinLeadConversion`).
    AWIN?: {
      Tracking?: {
        Sale?: Record<string, string>;
        run?: () => void;
      };
    };
  }
}

// Awin advertiser id. MasterTag: https://www.dwin1.com/130139.js
const AWIN_ADVERTISER_ID = "130139";
const AWIN_MASTERTAG_SRC = `https://www.dwin1.com/${AWIN_ADVERTISER_ID}.js`;
const AWIN_LEAD_AMOUNT = "2.50";
const AWIN_CURRENCY = "EUR";
// Keep the group and amount in sync with AWIN_EVENTS in api/src/lib/awin-client.ts.
const AWIN_COMMISSION_GROUP = "LEAD";
// Always "aw": every lead we report came through the affiliate MasterTag.
const AWIN_CHANNEL = "aw";

/** The one lead we report, in the shape both the Sale object and the pixel need. */
function buildLead(userId: string) {
  return {
    // Same orderRef as the S2S call (`<userId>-lead`), so Awin de-duplicates them.
    ref: `${userId}-lead`,
    amount: AWIN_LEAD_AMOUNT,
    currency: AWIN_CURRENCY,
  };
}

/**
 * Fire a single Awin lead conversion once a customer has completed onboarding.
 *
 * Sets the `AWIN.Tracking.Sale` object the MasterTag itself reads to build its
 * conversion call (tt=ia/js), and calls `Tracking.run()` because the MasterTag
 * is already loaded sitewide (see `loadAwinMasterTag`) by the time a customer
 * reaches this point — per Awin's own SPA guidance, a Sale object set after
 * the tag has already loaded needs an explicit `run()` to be picked up.
 * Setting `Sale` alone (with no `run()` available yet) is still correct if the
 * tag hasn't finished loading: it reads the object once it does.
 *
 * Also injects the mandatory Awin fallback pixel directly, independent of the
 * MasterTag, for visitors whose browser blocks it.
 *
 * Both are gated on Cookiebot marketing consent (Awin's cookies are marketing
 * cookies) and de-duped on a single localStorage flag keyed by `userId`, left
 * unclaimed until consent is granted so a later, consented visit still fires.
 */
export function fireAwinLeadConversion(userId: string | undefined | null) {
  if (typeof window === "undefined" || !userId) return;
  if (isSupportViewActive()) return;

  const flag = `awin_lead_conversion_fired:${userId}`;
  if (trackingFlagClaimed(flag)) return;
  if (!hasMarketingConsent()) return;
  claimTrackingFlag(flag);

  const lead = buildLead(userId);
  const parts = `${AWIN_COMMISSION_GROUP}:${lead.amount}`;

  window.AWIN = window.AWIN ?? {};
  window.AWIN.Tracking = window.AWIN.Tracking ?? {};
  window.AWIN.Tracking.Sale = {
    amount: lead.amount,
    channel: AWIN_CHANNEL,
    orderRef: lead.ref,
    parts,
    currency: lead.currency,
    test: "0",
  };
  window.AWIN.Tracking.run?.();

  const pixel = new Image();
  const params = new URLSearchParams({
    tt: "ns",
    tv: "2",
    merchant: AWIN_ADVERTISER_ID,
    amount: lead.amount,
    ch: AWIN_CHANNEL,
    ref: lead.ref,
    cr: lead.currency,
    parts,
    testmode: "0",
  });
  pixel.src = `https://www.awin1.com/sread.img?${params.toString()}`;
}

let awinMasterTagInjected = false;

/**
 * Load the Awin MasterTag sitewide, once, once marketing consent is granted.
 * Awin's tracking checker requires it present on every page (not just the
 * conversion page), so this runs from the root route rather than alongside
 * the lead conversion above. Retries on Cookiebot's own consent-ready event
 * for a visitor who grants consent after this first runs.
 */
export function loadAwinMasterTag() {
  if (typeof document === "undefined" || awinMasterTagInjected) return;
  if (isSupportViewActive()) return;

  const tryLoad = () => {
    if (awinMasterTagInjected || isSupportViewActive() || !hasMarketingConsent()) return;
    awinMasterTagInjected = true;
    document.removeEventListener("CookiebotOnConsentReady", tryLoad);
    const script = document.createElement("script");
    script.src = AWIN_MASTERTAG_SRC;
    script.async = true;
    document.head.appendChild(script);
  };

  tryLoad();
  document.addEventListener("CookiebotOnConsentReady", tryLoad);
}
